/**
 * recently-viewed.js — client-side "recently viewed" products (Agent 5)
 *
 * Storage: localStorage['theme:recently-viewed'] — JSON array of product handles,
 * most recent first, de-duplicated, capped at 12.
 *
 * On a product page the current handle is pushed to the front of the list. The
 * <recently-viewed-products> section then fetches `/products/<handle>.js` for the
 * remaining handles (the product being viewed is skipped) and renders the cards
 * itself. The whole section stays hidden while the list is empty.
 */
(function () {
  'use strict';

  // The asset is referenced by both `main-product` and the `recently-viewed`
  // section so that browsing history is recorded even when the section is not
  // on the page. Guard against the script running twice.
  if (window.RecentlyViewed) return;

  var STORAGE_KEY = 'theme:recently-viewed';
  var MAX_ITEMS = 12;

  /* ---------------------------------------------------------------- storage */

  function read() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(function (handle) {
        return typeof handle === 'string' && handle.length > 0;
      });
    } catch (error) {
      return [];
    }
  }

  function write(handles) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(handles.slice(0, MAX_ITEMS)));
    } catch (error) {
      /* Private mode / quota — recently viewed is a nice-to-have. */
    }
  }

  function push(handle) {
    if (!handle) return read();
    var handles = read().filter(function (item) {
      return item !== handle;
    });
    handles.unshift(handle);
    handles = handles.slice(0, MAX_ITEMS);
    write(handles);
    return handles;
  }

  function remove(handle) {
    write(
      read().filter(function (item) {
        return item !== handle;
      })
    );
  }

  /* ------------------------------------------------------------------ utils */

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function withWidth(src, width) {
    if (!src) return '';
    var url = src.indexOf('//') === 0 ? 'https:' + src : src;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  /**
   * Mirrors Shopify's money filter for every placeholder the money_format
   * setting can contain, so prices stay in the store currency (PKR).
   */
  function formatMoney(cents, format) {
    var value = typeof cents === 'string' ? cents.replace(/[^0-9.-]/g, '') : cents;
    value = Number(value);
    if (isNaN(value)) value = 0;

    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = (format || '').match(placeholder);
    var key = match ? match[1] : 'amount';

    function withDelimiters(number, precision, thousands, decimal) {
      number = (number / 100).toFixed(precision);
      var parts = number.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var fraction = parts[1] ? decimal + parts[1] : '';
      return whole + fraction;
    }

    var formatted;
    switch (key) {
      case 'amount_no_decimals':
        formatted = withDelimiters(value, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        formatted = withDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formatted = withDelimiters(value, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        formatted = withDelimiters(value, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        formatted = withDelimiters(value, 0, ' ', '.');
        break;
      case 'amount_with_space_separator':
        formatted = withDelimiters(value, 2, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        formatted = withDelimiters(value, 2, ' ', '.');
        break;
      default:
        formatted = withDelimiters(value, 2, ',', '.');
    }

    if (!format) return formatted;
    return format.replace(placeholder, formatted);
  }

  /* -------------------------------------------------------------- component */

  function RecentlyViewed(el) {
    this.el = el;
    this.list = el.querySelector('[data-recently-viewed-list]');
    this.note = el.querySelector('[data-recently-viewed-note]');
    this.currentHandle = el.dataset.currentProduct || '';
    this.limit = parseInt(el.dataset.productsToShow, 10) || 6;
    this.showWishlist = el.dataset.showWishlist === 'true';
    this.saleBadge = el.dataset.saleBadge || '';
    this.soldOut = el.dataset.soldOut || '';
    this.addToWishlist = el.dataset.addToWishlist || '';

    try {
      this.moneyFormat = JSON.parse(el.dataset.moneyFormat || '""');
    } catch (error) {
      this.moneyFormat = '';
    }

    var root = el.dataset.rootUrl || '/';
    this.rootUrl = root.replace(/\/+$/, '');

    if (this.list) this.render();
  }

  RecentlyViewed.prototype.render = function () {
    var self = this;
    var handles = read()
      .filter(function (handle) {
        return handle !== self.currentHandle;
      })
      .slice(0, this.limit);

    if (!handles.length) {
      this.reveal(false);
      return;
    }

    var requests = handles.map(function (handle) {
      return fetch(self.rootUrl + '/products/' + encodeURIComponent(handle) + '.js')
        .then(function (response) {
          if (!response.ok) throw new Error('Not found: ' + handle);
          return response.json();
        })
        .catch(function () {
          // Product unpublished or deleted — forget it so it is not fetched again.
          remove(handle);
          return null;
        });
    });

    Promise.all(requests).then(function (products) {
      var markup = products
        .filter(Boolean)
        .map(function (product) {
          return self.card(product);
        })
        .join('');

      self.list.innerHTML = markup;
      self.reveal(markup !== '');

      if (markup !== '' && window.Wishlist && typeof window.Wishlist.has === 'function') {
        Array.prototype.forEach.call(self.list.querySelectorAll('.wishlist-toggle'), function (button) {
          button.setAttribute('aria-pressed', window.Wishlist.has(button.dataset.productHandle) ? 'true' : 'false');
        });
      }
    });
  };

  RecentlyViewed.prototype.reveal = function (show) {
    var isEditor = !!(window.Shopify && window.Shopify.designMode);
    if (show || isEditor) {
      this.el.removeAttribute('hidden');
    } else {
      this.el.setAttribute('hidden', '');
    }
    if (this.note) this.note.hidden = show;
  };

  RecentlyViewed.prototype.card = function (product) {
    var url = this.rootUrl + '/products/' + product.handle;
    var image = product.featured_image || (product.images && product.images[0]) || '';
    var media = (product.media && product.media[0]) || null;
    var width = (media && media.preview_image && media.preview_image.width) || 800;
    var height = (media && media.preview_image && media.preview_image.height) || 1000;

    var price = formatMoney(product.price, this.moneyFormat);
    var compareAt = product.compare_at_price || 0;
    var onSale = compareAt > product.price;
    var percent = onSale ? Math.round(((compareAt - product.price) / compareAt) * 100) : 0;

    var badge = '';
    if (!product.available && this.soldOut) {
      badge =
        '<span class="recently-viewed__badge recently-viewed__badge--sold-out">' +
        escapeHtml(this.soldOut) +
        '</span>';
    } else if (onSale && percent > 0 && this.saleBadge) {
      badge =
        '<span class="recently-viewed__badge">' +
        escapeHtml(this.saleBadge.replace('[percent]', percent)) +
        '</span>';
    }

    var mediaMarkup = image
      ? '<img class="recently-viewed__image" src="' +
        escapeHtml(withWidth(image, 400)) +
        '" srcset="' +
        escapeHtml(withWidth(image, 200)) +
        ' 200w, ' +
        escapeHtml(withWidth(image, 400)) +
        ' 400w, ' +
        escapeHtml(withWidth(image, 600)) +
        ' 600w" sizes="(min-width: 750px) 25vw, 45vw" alt="' +
        escapeHtml(product.title) +
        '" width="' +
        width +
        '" height="' +
        height +
        '" loading="lazy">'
      : '<span class="recently-viewed__placeholder"></span>';

    var wishlist = '';
    if (this.showWishlist) {
      wishlist =
        '<button type="button" class="wishlist-toggle recently-viewed__wishlist" data-product-handle="' +
        escapeHtml(product.handle) +
        '" aria-pressed="false" aria-label="' +
        escapeHtml(this.addToWishlist) +
        '">' +
        '<span class="svg-wrapper" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" focusable="false">' +
        '<path d="M12 20.25S3.75 15.5 3.75 9.6A4.35 4.35 0 0 1 12 7.5a4.35 4.35 0 0 1 8.25 2.1c0 5.9-8.25 10.65-8.25 10.65Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>' +
        '</svg></span></button>';
    }

    var category = product.type
      ? '<p class="recently-viewed__category">' + escapeHtml(product.type) + '</p>'
      : '';

    var priceMarkup = '<span class="recently-viewed__price-current">' + escapeHtml(price) + '</span>';
    if (onSale) {
      priceMarkup +=
        '<s class="recently-viewed__price-compare">' +
        escapeHtml(formatMoney(compareAt, this.moneyFormat)) +
        '</s>';
    }

    return (
      '<li class="recently-viewed__item">' +
      '<div class="recently-viewed__card">' +
      badge +
      wishlist +
      '<a class="recently-viewed__media" href="' +
      escapeHtml(url) +
      '" tabindex="-1" aria-hidden="true">' +
      mediaMarkup +
      '</a>' +
      '<div class="recently-viewed__info">' +
      category +
      '<h3 class="recently-viewed__title"><a href="' +
      escapeHtml(url) +
      '">' +
      escapeHtml(product.title) +
      '</a></h3>' +
      '<div class="recently-viewed__price">' +
      priceMarkup +
      '</div>' +
      '</div>' +
      '</div>' +
      '</li>'
    );
  };

  /* ------------------------------------------------------------------- boot */

  function trackCurrentProduct() {
    var main = document.querySelector('product-info[id^="MainProduct"]');
    if (!main) return;

    var handleSource = main.querySelector('variant-selects[data-product-handle]');
    var handle = handleSource ? handleSource.dataset.productHandle : '';

    if (!handle) {
      var url = main.dataset.url || window.location.pathname;
      var match = url.match(/\/products\/([^/?#]+)/);
      if (match) handle = decodeURIComponent(match[1]);
    }

    push(handle);
  }

  function init(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    var nodes = root.querySelectorAll ? Array.prototype.slice.call(root.querySelectorAll('recently-viewed-products')) : [];
    if (root.matches && root.matches('recently-viewed-products')) nodes.push(root);

    nodes.forEach(function (el) {
      if (el.dataset.recentlyViewedReady === 'true') return;
      el.dataset.recentlyViewedReady = 'true';
      new RecentlyViewed(el);
    });
  }

  function boot() {
    trackCurrentProduct();
    init(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  window.RecentlyViewed = {
    get: read,
    push: push,
    clear: function () {
      write([]);
    },
  };
})();
